import { DateTime } from "luxon";
import React, { useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Box,
  IconButton,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import CustomDatePicker from "../../components/Inputs/DatePickers/CustomDatePicker";

import { Column } from "../../Layout/utils";
import { PickupOptions } from "../../services/Alert";
import { TooltipLabel } from "../../components/Tooltips";
import { checkErrors, getDefaultOptions } from "../../services/Category";
import { formInputsStyles } from "../../Configuration/styles";

export enum MODE {
  CUSTOM = "custom",
  CALENDAR_YEAR = "calendar-year",
  NEXT_CALENDAR_YEAR = "next-calendar-year",
  MONTH = "month",
  WEEK = "week",
  YEAR = "year",
}

type DefaultDateType = DateTime | undefined | null;

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    minWidth: "280px",
  },
  pickupBoxes: {
    width: "100%",
    marginTop: "1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    "& button": {
      flex: "1 1 0px",
      borderColor: theme.vars.palette.text.primary,
    },
  },
}));

export interface PickupParams {
  date_observation: DateTime;
  date_min_reservation: DateTime;
  date_max_reservation: DateTime;
}

/** Scope of the pickup dimension: the observation date and the booking-date
 *  bounds the perimeter filter (and the pickup tab) can select within. */
export const getPickupParams = async (): Promise<PickupParams> => {
  const response = await fetch(`/api/dimensions/pickup/`, getDefaultOptions());
  checkErrors(response);
  const result = await response.json();
  return {
    date_observation: DateTime.fromISO(result.date_observation),
    date_min_reservation: DateTime.fromISO(result.date_min_reservation),
    date_max_reservation: DateTime.fromISO(result.date_max_reservation),
  };
};

const getDateTimeOrNull = (date: string | undefined | null) => {
  return date ? DateTime.fromISO(date) : null;
};

const getISODateOrNull = (date: DateTime | null) => {
  return date ? date.toISODate() : null;
};

const getPickupDates = (options: PickupOptions) => {
  return {
    _start: getDateTimeOrNull(options?.date_reservation_start),
    _end: getDateTimeOrNull(options?.date_reservation_end),
  };
};

const formatDate = (prefix: string, date: DateTime): string => {
  return `${prefix} ${date.toLocaleString()}`;
};

const validateDate = (
  date: DateTime | null,
  defaultMinDate: DefaultDateType,
  defaultMaxDate: DefaultDateType
): boolean => {
  if (date) {
    if (!defaultMinDate || !defaultMaxDate) return false;
    if (date < defaultMinDate) return false;
    if (date > defaultMaxDate) return false;
  }
  return true;
};

const ErrorMessage = styled("span")(({ theme }: { theme: Theme }) => ({
  fontSize: "0.9rem",
  color: theme.vars.palette.error.main,
}));

const SectionHeaderBlock = ({
  title,
  deleteDate,
  minDate,
  maxDate,
}: {
  title: string;
  deleteDate: () => void;
  minDate?: DateTime;
  maxDate?: DateTime;
}) => {
  return (
    <>
      <Typography
        variant="subtitle1"
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: "bold",
        }}
      >
        <FormattedMessage id={title} />
        <IconButton onClick={deleteDate}>
          <ReplayIcon />
        </IconButton>
      </Typography>
      {(minDate || maxDate) && (
        <Typography variant="caption" component="div">
          {minDate && formatDate("min.", minDate)}
          {"   "}
          {maxDate && formatDate("max.", maxDate)}
        </Typography>
      )}
    </>
  );
};

export const CreateFilterPickupForm = ({
  data,
  onSubmit,
}: {
  data: PickupOptions;
  onSubmit: (data: PickupOptions) => void;
}) => {
  const intl = useIntl();
  const { classes } = useStyles();
  const inputStyles = formInputsStyles();

  const [alignment, setAlignment] = useState<string | null>(null);

  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);

  const [isStartDateValid, setIsStartDateValid] = useState<boolean>(true);
  const [isEndDateValid, setIsEndDateValid] = useState<boolean>(true);

  // Pickup params - retrieve scope dates
  const query = useQuery(["pickup"], () => getPickupParams());

  const changeStartDate = useCallback(
    (date: DateTime | null) => {
      const isValid = validateDate(
        date,
        query.data?.date_min_reservation,
        query.data?.date_max_reservation
      );

      setStartDate(isValid ? date : null);
      setIsStartDateValid(isValid);
    },
    [query.data]
  );

  const changeEndDate = useCallback(
    (date: DateTime | null) => {
      const isValid = validateDate(
        date,
        query.data?.date_min_reservation,
        query.data?.date_max_reservation
      );

      setEndDate(isValid ? date : null);
      setIsEndDateValid(isValid);
    },
    [query.data]
  );

  const deleteDates = () => {
    setAlignment(null);
    changeStartDate(null);
    changeEndDate(null);
  };

  const handleAlignment = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    event.preventDefault();
    const maxDate = query.data?.date_max_reservation;
    if (!maxDate || !newAlignment) return false;
    const date = maxDate.plus({ days: -(parseInt(newAlignment) - 1) });
    changeStartDate(date);
    changeEndDate(query.data?.date_max_reservation || null);
    setAlignment(newAlignment);
  };

  const handleSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const params: PickupOptions = {
      date_observation: null,
      date_reservation_start: getISODateOrNull(startDate),
      date_reservation_end: getISODateOrNull(endDate),
    };
    onSubmit(params);
  };

  useEffect(() => {
    const { _start, _end } = getPickupDates(data);

    changeStartDate(_start);
    changeEndDate(_end);
  }, [data, changeStartDate, changeEndDate]);

  if (!query.data)
    return <FormattedMessage id="FAILED TO RETRIEVE PICKUP PARAMETERS" />;

  return (
    <form
      id="create-filter-form"
      className={classes.root}
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <Column
          sx={{
            gap: 0,
          }}
        >
          <SectionHeaderBlock
            title="DATE_RESERVATION"
            deleteDate={deleteDates}
            minDate={query.data.date_min_reservation}
            maxDate={query.data.date_max_reservation}
          />

          <Box
            className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
          >
            <ToggleButtonGroup
              value={alignment}
              exclusive
              size="small"
              onChange={handleAlignment}
              aria-label="text alignment"
              className={classes.pickupBoxes}
            >
              <ToggleButton value="1" aria-label="left aligned">
                <Typography variant="subtitle1" component="span">
                  -1
                </Typography>
              </ToggleButton>
              <ToggleButton value="3" aria-label="left aligned">
                <Typography variant="subtitle1" component="span">
                  -3
                </Typography>
              </ToggleButton>
              <ToggleButton value="7" aria-label="left aligned">
                <Typography variant="subtitle1" component="span">
                  -7
                </Typography>
              </ToggleButton>
              <ToggleButton value="14" aria-label="left aligned">
                <Typography variant="subtitle1" component="span">
                  -14
                </Typography>
              </ToggleButton>
              <ToggleButton value="21" aria-label="left aligned">
                <Typography variant="subtitle1" component="span">
                  -21
                </Typography>
              </ToggleButton>
              <ToggleButton value="28" aria-label="left aligned">
                <Typography variant="subtitle1" component="span">
                  -28
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box
            className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
          >
            <CustomDatePicker
              label="DATE_START"
              minDate={query.data.date_min_reservation}
              maxDate={query.data.date_max_reservation}
              value={startDate}
              onChange={(isoStr) =>
                changeStartDate(isoStr ? DateTime.fromISO(isoStr) : null)
              }
            />
          </Box>
          <Box
            className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
          >
            <CustomDatePicker
              label={
                <TooltipLabel
                  label={intl.formatMessage({ id: "DATE_END" })}
                  tooltipLabel={intl.formatMessage({ id: "DATE_INCLUDED" })}
                  translateTooltip={false}
                />
              }
              translateLabel={false}
              minDate={query.data.date_min_reservation}
              maxDate={query.data.date_max_reservation}
              value={endDate}
              onChange={(isoStr) =>
                changeEndDate(isoStr ? DateTime.fromISO(isoStr) : null)
              }
            />
          </Box>
          {!isStartDateValid && (
            <ErrorMessage>
              {intl.formatMessage({ id: "filters.error.min" })}
            </ErrorMessage>
          )}
          {!isEndDateValid && (
            <ErrorMessage>
              {intl.formatMessage({ id: "filters.error.max" })}
            </ErrorMessage>
          )}
        </Column>
      </Box>
    </form>
  );
};
